import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HeaderService } from '../../core/header/header.service';
import { ShopInfoService } from '../../core/shop-info/shop-info.service';
import { ShopInfo } from '../../shop-landing-page/shared/models/shop-info.model';
import {
    CheckAvailabilityOfShopUid, FetchShopInfo,
    ResetIsShopUidAvailableFlag, ResetUpdatedShopUid, UpdateShopInfo, UploadShopProfileImage
} from './shop-info.actions';

export interface ShopInfoStateModel {
    loading: boolean;
    data: ShopInfo;
    error: HttpErrorResponse;
    isShopUidAvailable: boolean;
    updatedShopUid: string;
}
@State<ShopInfoStateModel>({
    name: 'ShopInfo',
    defaults: {
        loading: false,
        data: new ShopInfo(),
        error: null,
        isShopUidAvailable: null,
        updatedShopUid: null
    }
})
@Injectable()
export class ShopInfoState {
    constructor(
        private shopInfoService: ShopInfoService,
        private headerService: HeaderService
    ) { }

    /**
     *
     *
     * @name getLoader
     * @description This method is used to select loading state
     * @static
     * @param state
     * @returns
     * @memberof ShopInfoState
     */
    @Selector()
    static getLoader(state: ShopInfoStateModel) {
        return state.loading;
    }

    /**
     *
     *
     * @name getShopInfo
     * @description This method is used to select shopInfo data from state
     * @static
     * @param state
     * @memberof ShopInfoState
     */
    @Selector()
    static getShopInfo(state: ShopInfoStateModel) {
        return state.data;
    }

    /**
     *
     *
     * @name detectError
     * @description This method is used to detect error
     * @static
     * @param state
     * @memberof ShopInfoState
     */
    @Selector()
    static detectError(state: ShopInfoStateModel) {
        return state.error;
    }

    /**
     *
     *
     * @description to check if shop uid availability state
     * @static
     * @param state
     * @returns
     * @memberof ShopInfoState
     */
    @Selector()
    static isShopUidAvailable(state: ShopInfoStateModel) {
        return state.isShopUidAvailable;
    }

    /**
     *
     *
     * @description to get the updated shop uid from state
     * @static
     * @param state
     * @returns
     * @memberof ShopInfoState
     */
    @Selector()
    static getUpdatedShopUid(state: ShopInfoStateModel) {
        return state.updatedShopUid;
    }

    /**
     *
     *
     * @name fetchShopInfo
     * @description This action is used to fetch shop Info
     * @param ctx
     * @param action
     * @memberof ShopInfoState
     */
    @Action(FetchShopInfo)
    fetchShopInfo(ctx: StateContext<ShopInfoStateModel>, action: FetchShopInfo) {
        this.startLoader(ctx);
        return this.shopInfoService.getShopInfo(action.payload).pipe(
            tap(shopInfo => {
                const state = ctx.getState();
                this.headerService.shopProfilePictureUrl$.next(shopInfo?.profilePicture?.downloadUrl);
                ctx.setState({
                    ...state,
                    loading: false,
                    data: shopInfo
                });
            }),
            catchError(err => {
                this.logError(ctx, err);
                const state = ctx.getState();
                ctx.setState({
                    ...state,
                    loading: false,
                    data: null,
                    error: err
                });
                return throwError(err);
            }));
    }

    /**
     *
     *
     * @name updateShopInfo
     * @description This action is used to update shop Info
     * @param ctx
     * @param action
     * @returns
     * @memberof ShopInfoState
     */
    @Action(UpdateShopInfo)
    updateShopInfo(ctx: StateContext<ShopInfoStateModel>, action: UpdateShopInfo) {
        this.startLoader(ctx);
        return this.shopInfoService.updateShopInfo(action.payload).pipe(
            tap(shopInfo => {
                const state = ctx.getState();
                const isShopUidUpdated = this.isShopUidUpdated(state, shopInfo);
                if (isShopUidUpdated) {
                    this.shopInfoService.updateJwtToken(shopInfo);
                }
                ctx.setState({
                    ...state,
                    loading: false,
                    data: shopInfo,
                    updatedShopUid: isShopUidUpdated ? shopInfo.shopUid : null
                });
                ctx.dispatch(new ResetUpdatedShopUid());
            }),
            catchError(err => {
                this.logError(ctx, err);
                return throwError(err);;
            }));
    }

    /**
     *
     *
     * @description to check if shop uid is available or not
     * @param ctx
     * @param action
     * @returns
     * @memberof ShopInfoState
     */
    @Action(CheckAvailabilityOfShopUid)
    checkAvailabilityOfShopUid(ctx: StateContext<ShopInfoStateModel>, action: CheckAvailabilityOfShopUid) {
        this.startLoader(ctx);
        return this.shopInfoService.checkAvailabilityOfShopUid(action.payload.shopUid).pipe(
            tap(response => {
                const state = ctx.getState();
                ctx.setState({
                    ...state,
                    loading: false,
                    isShopUidAvailable: response
                });
                ctx.dispatch(new ResetIsShopUidAvailableFlag());
            }),
            catchError(err => {
                this.logError(ctx, err);
                return throwError(err);;
            })
        );
    }

    /**
     *
     *
     * @description to reset the shop uid available flag
     * @param ctx
     * @memberof ShopInfoState
     */
    @Action(ResetIsShopUidAvailableFlag)
    resetIsShopUidAvailableFlag(ctx: StateContext<ShopInfoStateModel>) {
        ctx.patchState({
            isShopUidAvailable: null
        });
    }

    /**
     *
     *
     * @description to reset the updated shop uid
     * @param ctx
     * @memberof ShopInfoState
     */
    @Action(ResetUpdatedShopUid)
    resetUpdatedShopUid(ctx: StateContext<ShopInfoStateModel>) {
        ctx.patchState({
            updatedShopUid: null
        });
    }

    @Action(UploadShopProfileImage)
    uploadShopProfileImage(ctx: StateContext<ShopInfoStateModel>, action: UploadShopProfileImage) {
        this.startLoader(ctx);
        return this.shopInfoService.uploadShopProfileImage(action.payload.shopId, action.payload.file).pipe(
            tap(response => {
                const state = ctx.getState();
                state.data.profilePicture = response;
                this.headerService.shopProfilePictureUrl$.next(response?.downloadUrl);
                ctx.patchState({
                    ...state,
                    loading: false
                });
            }),
            catchError(err => {
                this.logError(ctx, err);
                return throwError(err);;
            })
        );
    }

    /**
     *
     *
     * @description to check if shopUid is updated or not
     * @param state
     * @param shopInfo
     * @returns
     * @memberof ShopInfoState
     */
    isShopUidUpdated(state: ShopInfoStateModel, shopInfo: ShopInfo): boolean {
        return (state && state.data && state.data.shopUid && shopInfo && shopInfo.shopUid && state.data.shopUid !== shopInfo.shopUid);
    }

    /**
     *
     *
     * @name startLoader
     * @description This method is used to patch state loading
     * @param ctx
     * @memberof ShopInfoState
     */
    startLoader(ctx: StateContext<ShopInfoStateModel>) {
        ctx.patchState({
            loading: true
        });
    }

    /**
     *
     *
     * @name logError
     * @description This method is used to patch state with error
     * @param ctx
     * @param err
     * @memberof ShopInfoState
     */
    logError(ctx: StateContext<ShopInfoStateModel>, err: any) {
        ctx.patchState({
            loading: false,
            error: err
        });
    }
}
